import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { alertsApi } from '../api/alerts.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { Badge } from '../components/ui/Badge.jsx';
import { PlateNumber } from '../components/ui/PlateNumber.jsx';
import { formatDate, daysUntil, expiryStatus } from '../utils/dates.js';
import { documentLabel } from '../api/documents.js';

function groupByDay(alerts) {
  const groups = new Map();
  for (const a of alerts) {
    const d = new Date(a.sent_at);
    const key = d.toISOString().slice(0, 10);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([date, items]) => ({ date, items }));
}

function dayLabel(iso) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  if (iso === today) return 'Astăzi';
  if (iso === yesterday) return 'Ieri';
  return new Date(iso).toLocaleDateString('ro-RO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

function UrgencyBadge({ doc }) {
  const s = expiryStatus(doc.data_expirare);
  const d = daysUntil(doc.data_expirare);
  if (s === 'expired') return <Badge tone="danger">Expirat {-d}z</Badge>;
  if (s === 'danger') return <Badge tone="danger">În {d}z</Badge>;
  if (s === 'warn') return <Badge tone="warn">În {d}z</Badge>;
  return <Badge tone="muted">—</Badge>;
}

function AlertItem({ alert }) {
  const doc = alert.document;
  const resolved = Boolean(doc?.resolved_at);
  const timeStr = new Date(alert.sent_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });

  return (
    <Link
      to={doc?.car ? `/cars/${doc.car.id}` : '#'}
      className={`flex items-start gap-3 rounded-card border p-3 transition-all hover:shadow-card ${
        resolved ? 'border-border bg-white opacity-60' : 'border-border bg-white hover:-translate-y-0.5'
      }`}
    >
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${resolved ? 'bg-ok/10 text-ok' : 'bg-coral/10 text-coral'}`}>
        {resolved ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`font-semibold ${resolved ? 'text-muted line-through' : 'text-ink'}`}>
            {doc ? documentLabel(doc.tip) : 'Document șters'}
          </span>
          {doc && !resolved && <UrgencyBadge doc={doc} />}
          {resolved && <Badge tone="ok">Rezolvat</Badge>}
        </div>
        {doc?.car && (
          <div className="mt-1 flex items-center gap-2">
            <PlateNumber value={doc.car.numar_inmatriculare} size="sm" />
            <span className="text-xs text-muted">{doc.car.marca} {doc.car.model}</span>
          </div>
        )}
        {doc?.data_expirare && (
          <div className="mt-1 font-mono text-xs text-muted">
            Expiră {formatDate(doc.data_expirare)}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 font-mono text-[11px] text-muted">{timeStr}</div>
    </Link>
  );
}

export function Alerts() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [alerts, setAlerts] = useState(null);
  const [err, setErr] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState('');

  async function load() {
    try {
      const { alerts } = await alertsApi.list();
      setAlerts(alerts);
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    }
  }

  useEffect(() => { load(); }, []);

  async function triggerNow() {
    setTriggering(true);
    setTriggerMsg('');
    try {
      const { stats } = await alertsApi.trigger();
      setTriggerMsg(
        `Verificare rulată: ${stats.notifications} notificări noi · emailuri: ${stats.emailsSent}/${stats.emailsSent + stats.emailsFailed}`
      );
      await load();
    } catch (ex) {
      setTriggerMsg('Eroare: ' + (ex.body?.error || ex.message));
    } finally {
      setTriggering(false);
    }
  }

  const groups = alerts ? groupByDay(alerts) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-ink">Alerte</h2>
        {isAdmin && (
          <button onClick={triggerNow} disabled={triggering} className="btn-ghost border border-border text-sm">
            {triggering ? 'Se verifică…' : 'Verifică acum'}
          </button>
        )}
      </div>

      <p className="text-sm text-muted">
        Zilnic la 10:00 verificăm documentele care urmează să expire și îți afișăm aici alertele.
        {isAdmin && <> Ca admin, vezi toate documentele din flotă.</>}
      </p>

      {triggerMsg && (
        <div className="rounded-lg border border-teal/20 bg-teal/5 px-3 py-2 text-sm text-teal-bright">
          {triggerMsg}
        </div>
      )}

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      {alerts === null && <div className="card text-center text-muted">Se încarcă…</div>}

      {alerts && alerts.length === 0 && (
        <div className="card text-center text-sm text-muted">
          <p>Nicio alertă încă.</p>
          <p className="mt-2 text-xs">
            Verificarea automată rulează zilnic la 10:00 (Europe/Bucharest). Dacă ai documente care expiră în ≤5 zile, primești alertă aici.
          </p>
        </div>
      )}

      {groups.map(({ date, items }) => (
        <section key={date} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
            {dayLabel(date)}
          </h3>
          <div className="space-y-2">
            {items.map((a) => <AlertItem key={a.id} alert={a} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
